// PYK Ekranı Ayarları penceresi kullanım rehberi. Metinler 'guides' çeviri alanında.
import type { TFunction } from 'i18next';
import type { GuideContent } from '../../components/common/UsageGuideDialog';

const G = 'sales.pykSettings';

export const salesPykSettingsGuide = (t: TFunction): GuideContent => ({
  layout: 'tabbed',
  title: t(`${G}.title`, 'PYK ekranı nasıl ayarlanır?'),
  intro: t(`${G}.intro`, 'Bu pencerede PYK ekranının ödeme yöntemlerini, kasiyerlerini, yazarkasa cihazını ve görünüm ayarlarını belirlersiniz. Haritada bir bölgeye tıklayarak o adıma geçebilirsiniz.'),
  regionLabels: {
    header: t(`${G}.regions.header`, 'Başlık'),
    tabs: t(`${G}.regions.tabs`, 'Ayar sekmeleri'),
    body: t(`${G}.regions.body`, 'Sekme içeriği'),
    footer: t(`${G}.regions.footer`, 'İptal / Kaydet'),
  },
  navItems: [
    t(`${G}.nav.payment`, 'Ödeme Yöntemleri'),
    t(`${G}.nav.login`, 'Login İşlemleri'),
    t(`${G}.nav.fiscal`, 'Yazarkasa Ayarları'),
    t(`${G}.nav.other`, 'Diğer Ayarlar'),
  ],
  steps: [
    {
      region: 'tabs',
      title: t(`${G}.s1.title`, '1. Ödeme yöntemlerini tanımlayın'),
      text: t(`${G}.s1.text`, '"Ödeme Yöntemleri" sekmesinde "+ Nakit Ekle" ile nakit, "+ POS Ekle" ile POS yöntemi ekleyin. "Bağlantı" sütununda nakit için kasayı, POS için POS özellikli banka hesabını veya alt hesabını seçin. "Durum" anahtarı yöntemi açıp kapatır, çöp kutusu siler.'),
      tips: [
        t(`${G}.s1.tip1`, 'PYK ekranında NAKİT ile yüklenen bakiye bu kasaya, POS ile yüklenen bakiye bu banka hesabına kaydedilir; nakit bakiye iadesi de bu kasadan yapılır.'),
        t(`${G}.s1.tip2`, 'Yalnız bir nakit yöntemi eklenebilir.'),
        t(`${G}.s1.tip3`, 'Uygun kasa veya POS hesabı yoksa bağlantı alanının yanındaki hızlı ekleme düğmesiyle bu ekranın şubesine yeni kasa / POS hesabı açabilirsiniz.'),
        t(`${G}.s1.tip4`, 'Hiç ödeme yöntemi tanımlanmazsa PYK ekranında bakiye yüklenemez.'),
      ],
    },
    {
      region: 'body',
      title: t(`${G}.s2.title`, '2. Personel ve kasiyerleri belirleyin'),
      text: t(`${G}.s2.text`, '"Login İşlemleri" sekmesinde ekranı kullanacak personeli seçin; listeden bir personele tıklayıp sağdaki anahtarlarla yetkilerini verin. "Kasiyer sistemi kullan (PIN ile giriş)" açılırsa PYK ekranı açılışında kasiyer PIN\'i sorulur; PIN girilmeden bakiye yükleme, iade ve oturum işlemleri yapılamaz, işlemler kasiyer adına kaydedilir.'),
      tips: [
        t(`${G}.s2.tip1`, 'Kasiyer eklerken ad ve en az 4 haneli şifre (PIN) girin. PYK yetkileri: Bakiye Yükleme, Bakiye İade, Oturum Başlatma / Kapatma.'),
        t(`${G}.s2.tip2`, 'Kasiyer ekleme, düzenleme ve silme kendi düğmeleriyle hemen kaydedilir.'),
      ],
    },
    {
      region: 'body',
      title: t(`${G}.s3.title`, '3. Cihaz kodu üretin'),
      text: t(`${G}.s3.text`, 'Aynı sekmenin altındaki "Cihaz Kodları" bölümünde, bu ekranı personel girişi olmadan açacak cihazlar için 11 haneli kod üretirsiniz ("Kod Üret"). Kodu cihazdaki local-service üzerinde "Satış Ekranı Bağla" sayfasına girin.'),
      tips: [
        t(`${G}.s3.tip1`, 'Kod üretebilmek için önce ekran ayarlarını kaydedin.'),
        t(`${G}.s3.tip2`, 'Bir kodu silerseniz kod geçersizleşir; o kodla açılmış ekranlar oturumunu kaybeder.'),
      ],
    },
    {
      region: 'body',
      title: t(`${G}.s4.title`, '4. Yazarkasa cihazını seçin'),
      text: t(`${G}.s4.text`, '"Yazarkasa Ayarları" sekmesinde "Yazarkasa" listesinden Yok, PavoPay POS veya Ingenico 5000F seçin. Seçim yalnız POS ile bakiye yükleme ve iadeyi etkiler.'),
      tips: [
        t(`${G}.s4.tip1`, 'Yok: POS ile yüklenen bakiye doğrudan banka hesabına işlenir, yazarkasa cihazı kullanılmaz.'),
        t(`${G}.s4.tip2`, 'PavoPay POS: POS ile bakiye yüklemede cihazdan avans POS ödemesi alınır. "Rest-P2P Modu (Uzaktan PavoPay)" açılırsa POS bakiye yükleme ve POS avans iadesi "Cihaz Seç" ile seçilen Pavo cihazına gönderilir; cihaz seçilmezse çalışmaz. Pavo cihaz uygulaması güncel sürümde olmalıdır.'),
      ],
    },
    {
      region: 'body',
      title: t(`${G}.s5.title`, '5. Ingenico için bakiye yükleme ürünü'),
      text: t(`${G}.s5.text`, 'Ingenico 5000F seçildiyse "Bakiye Yükleme Ürünü (POS satışı için)" alanına ürün adı yazıp bir ürün seçin. POS ile bakiye yüklemede Ingenico\'da bu ürün, yüklenen tutar kadar satılır (miktar 1, fiyat = tutar; KDV ve departman üründen alınır). Nakit yüklemede cihaza bir şey gönderilmez, fiş kesilmez.'),
      tips: [
        t(`${G}.s5.tip1`, 'Ürün seçilmezse POS ile bakiye yüklenemez.'),
        t(`${G}.s5.tip2`, 'Ingenico\'da bakiye iadesi her zaman nakit olarak kasadan yapılır; Ingenico\'ya iade veya fiş gönderilmez.'),
      ],
    },
    {
      region: 'body',
      title: t(`${G}.s6.title`, '6. Diğer ayarlar'),
      text: t(`${G}.s6.text`, '"Diğer Ayarlar" sekmesinde "Oturum Başlat / Oturum Kapat butonlarını göster" açıkken personel PYK ekranında kart için oturum açıp kapatabilir. Kapatırsanız bu düğmeler ekranda görünmez.'),
    },
    {
      region: 'footer',
      title: t(`${G}.s7.title`, '7. Kaydedin'),
      text: t(`${G}.s7.text`, '"Değişiklikleri Kaydet" tüm sekmelerdeki ayarları birlikte kaydeder ve "Ayarlar başarıyla kaydedildi" yazar. "İptal" kaydetmeden kapatır. Yeni ayarlar PYK ekranı yeniden açıldığında geçerli olur. Sağ üstteki (i) bu rehberi yeniden açar.'),
    },
  ],
});
